import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidSlug, slugify } from "../../shared/slug";
import { api } from "../api";
import { useShell } from "./AdminShell";
import ProductForm, { emptyProduct, type ProductDraft } from "./ProductForm";

/** Guided entry point: product, then its first demo, then into the demo wizard. */
export default function NewProductWizard() {
  const { refreshNav } = useShell();
  const nav = useNavigate();
  const [stage, setStage] = useState<1 | 2>(1);
  const [product, setProduct] = useState<ProductDraft>(emptyProduct);
  const [demo, setDemo] = useState({ title: "", slug: "" });
  const [demoSlugTouched, setDemoSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const productOk = product.name.trim() && isValidSlug(product.slug);
  const demoOk = demo.title.trim() && isValidSlug(demo.slug);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await api.createProduct(product);
      const d = await api.createDemo(p.id, demo);
      await refreshNav();
      nav(`/admin/demos/${d.id}/survey?new=1`);
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      <div className="page-head">
        <div>
          <h1>Add a new product</h1>
          <p className="sub">{stage === 1 ? "Step 1 of 2: the product" : "Step 2 of 2: its first demo"}. After this, the demo wizard walks you through the survey, screens and hotspots.</p>
        </div>
      </div>
      <div className="panel stack">
        {stage === 1 ? (
          <>
            <ProductForm value={product} onChange={setProduct} />
            {product.slug && !isValidSlug(product.slug) && <p className="error-box">That URL name is reserved. Pick another.</p>}
            <div className="row"><span className="spacer" /><button className="btn btn-primary" disabled={!productOk} onClick={() => setStage(2)}>Continue to demo</button></div>
          </>
        ) : (
          <>
            <div className="grid-2">
              <label className="field">
                <span>Demo title</span>
                <input type="text" autoFocus value={demo.title} placeholder="Endpoint protection tour" onChange={(e) => setDemo({ title: e.target.value, slug: demoSlugTouched ? demo.slug : slugify(e.target.value) })} />
              </label>
              <label className="field">
                <span>URL name <span className="hint">/{product.slug}/{demo.slug || "…"}</span></span>
                <input type="text" value={demo.slug} onChange={(e) => { setDemoSlugTouched(true); setDemo({ ...demo, slug: slugify(e.target.value) }); }} />
              </label>
            </div>
            {error && <div className="error-box">{error}</div>}
            <div className="row">
              <button className="btn btn-ghost" onClick={() => setStage(1)}>Back</button>
              <span className="spacer" />
              <button className="btn btn-primary" disabled={!demoOk || busy} onClick={create}>{busy ? "Creating…" : "Create and continue"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
